import {
    Box,
    Typography,
    useTheme,
    CircularProgress
} from '@mui/material';
import React, { useEffect } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import PortalCard from '../../../../../components/PortalCard';
import analyticsService from '../../../../../services/analytics/AnalyticsService';

interface CategoryPerformanceChartProps {
    onDataLoad?: (categoryCount: number) => void;
}

const CategoryPerformanceChart: React.FC<CategoryPerformanceChartProps> = ({ onDataLoad }) => {
    const theme = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState<Array<{
        category: string;
        productCount: number;
        totalViews: number;
        uniqueViews: number;
        avgViewsPerProduct: number;
        productsOnSale: number;
        totalStock: number;
        lowStockCount: number;
    }>>([]);
    const [error, setError] = React.useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await analyticsService.getCategoryPerformance();
                if (result.success && result.data) {
                    setData(result.data);
                    if (onDataLoad) {
                        onDataLoad(result.data.length);
                    }
                } else {
                    setError(result.message || 'Failed to fetch category performance');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to fetch category performance');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const categories = data.map(item => item.category);
    const viewCounts = data.map(item => item.totalViews);

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    Category Performance
                </Typography>
                <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                    Total views by category
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 300, width: '100%' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='error' fontFamily='primaryFont'>
                            {error}
                        </Typography>
                    </Box>
                ) : data.length > 0 ? (
                    <BarChart
                        xAxis={[{
                            data: categories,
                            scaleType: 'band',
                            tickLabelStyle: {
                                fontSize: '0.875rem',
                                fill: theme.palette.text.primary,
                                fontWeight: 500,
                                fontFamily: 'primaryFont'
                            }
                        }]}
                        series={[{
                            data: viewCounts,
                            label: 'Views',
                            color: theme.palette.primary.main,
                        }]}
                        height={400}
                        margin={{ top: 20, right: 30, bottom: 100, left: 60 }}
                        layout='vertical'
                        slotProps={{
                            legend: { hidden: true }
                        }}
                        sx={{
                            '& .MuiChartsAxis-tickLabel': {
                                fontSize: '0.875rem',
                                fill: theme.palette.text.secondary,
                                fontFamily: 'primaryFont',
                            },
                            '& .MuiChartsAxis-line': {
                                stroke: theme.palette.divider,
                            },
                            '& .MuiBarElement-root': {
                                rx: 4,
                            },
                        }}
                    />
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                            No data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default CategoryPerformanceChart;

